import { useEffect } from 'react';

import type { Noop } from '@enterprise/core';

import { useSyncedRef } from '#misc/synced-ref/synced-ref.hook';

/**
 * React hook that invokes a callback function when the component is unmounted.
 *
 * @param callback - A function to be called when the component unmounts.
 *
 * @remarks
 * The callback is kept in sync with the latest reference using `useSyncedRef`.
 * This ensures that the most recent version of the callback is executed on unmount.
 *
 * @example
 * ```tsx
 * useDidUnmount(() => {
 *   // Cleanup logic here
 * });
 * ```
 */
export const useDidUnmount = (callback: Noop) => {
  const ref = useSyncedRef(callback);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the ref is updated in useSyncedRef
  useEffect(() => {
    return () => {
      ref.current();
    };
  }, []);
};
